<template>
    <KCard
        :as="as"
        :variant="variant === 'outline' ? 'outline' : 'plain'"
        padding="none"
        :class="block(modifier(variant), modifier(orientation))"
    >
        <div
            v-if="$slots.media"
            :class="element('media')"
        >
            <slot name="media" />

            <KEyebrow
                v-if="$slots.eyebrow"
                variant="pill"
                :class="element('eyebrow')"
            >
                <slot name="eyebrow" />
            </KEyebrow>
        </div>

        <div :class="element('body')">
            <div
                v-if="$slots.meta"
                :class="element('meta')"
            >
                <slot name="meta" />
            </div>

            <component
                :is="titleAs"
                v-if="$slots.title || title"
                class="k-heading-5"
                :class="element('title')"
            >
                <slot name="title">
                    {{ title }}
                </slot>
            </component>

            <div
                v-if="$slots.default || text"
                class="k-text"
                :class="element('text')"
            >
                <slot>{{ text }}</slot>
            </div>

            <div
                v-if="$slots.content"
                :class="element('content')"
            >
                <slot name="content" />
            </div>

            <div
                v-if="$slots.price || $slots.action"
                :class="element('footer')"
            >
                <div
                    v-if="$slots.price"
                    :class="element('price')"
                >
                    <slot name="price" />
                </div>

                <div
                    v-if="$slots.action"
                    :class="element('action')"
                >
                    <slot name="action" />
                </div>
            </div>
        </div>
    </KCard>
</template>

<script setup lang="ts">
import type { KTeaserCardProps } from './types'
import useBem from '@yeeroen/use-bem'

import KCard from '../card/k-card.vue'
import KEyebrow from '../eyebrow/k-eyebrow.vue'

defineOptions({ name: 'KTeaserCard' })

withDefaults(defineProps<KTeaserCardProps>(), {
    as: 'article',
    titleAs: 'h3',
    variant: 'outline',
    orientation: 'vertical',
})

defineSlots<{
    default?: () => unknown
    media?: () => unknown
    eyebrow?: () => unknown
    meta?: () => unknown
    title?: () => unknown
    content?: () => unknown
    price?: () => unknown
    action?: () => unknown
}>()

const { block, element, modifier } = useBem('k-teaser-card')
</script>

<style lang="scss">
@use './k-teaser-card.scss';
</style>
